<style scoped></style>

<template>
  <div>
    <div class="flex justify-between">
      <div class="flex-shrink-0 flex items-center">
        <svg
          class="w-2 mr-2 text-gray-300 cursor-move"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
        >
          <path
            fill="currentColor"
            d="M96 32H32C14.33 32 0 46.33 0 64v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V64c0-17.67-14.33-32-32-32zm0 160H32c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zm0 160H32c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zM288 32h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V64c0-17.67-14.33-32-32-32zm0 160h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32zm0 160h-64c-17.67 0-32 14.33-32 32v64c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32v-64c0-17.67-14.33-32-32-32z"
          />
        </svg>
      </div>
      <div class="flex-1">
        <div v-if="!this.isEditing" class="text-sm">
          <h2
            v-if="field.element == 'h2'"
            class="text-2xl leading-6 font-semibold text-gray-900"
          >
            {{ field.value }}
          </h2>
          <h3
            v-if="field.element == 'h3'"
            class="text-lg leading-6 font-medium text-gray-700"
          >
            {{ field.value }}
          </h3>
          <h4
            v-if="field.element == 'h4'"
            class="text-sm leading-5 font-medium text-gray-700"
          >
            {{ field.value }}
          </h4>
          <div v-if="field.element == 'field'">
            <span class="text-gray-500">{{ fieldType(field.type) }}</span>
            {{ field.label }}{{ field.required ? '*' : '' }}
          </div>
          <span v-if="field.element == 'p'">{{ field.value }}</span>
        </div>
        <div v-if="this.isEditing" class="text-sm">
          <select
            :id="`field-${field.id}-element`"
            :name="`field-${field.id}-element`"
            v-model="form.element"
            class="ml-1 form-select transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option value="field">{{ $t('fields.field') }}</option>
            <option value="h2">{{ $t('fields.header_2') }}</option>
            <option value="h3">{{ $t('fields.header_3') }}</option>
            <option value="h4">{{ $t('fields.header_4') }}</option>
            <option value="p">{{ $t('fields.paragraph') }}</option>
          </select>
          <select
            v-if="form.element == 'field'"
            :id="`field-${field.id}-type`"
            :name="`field-${field.id}-type`"
            v-model="form.type"
            class="ml-1 form-select transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option value="">{{ fieldType('') }}</option>
            <option value="longtext">{{ fieldType('longtext') }}</option>
            <option value="email">{{ fieldType('email') }}</option>
            <option value="number">{{ fieldType('number') }}</option>
            <option value="date">{{ fieldType('date') }}</option>
            <option value="datetime-local">
              {{ fieldType('datetime-local') }}
            </option>
            <option value="dropdown">{{ fieldType('dropdown') }}</option>
            <option value="checkbox">{{ fieldType('checkbox') }}</option>
            <option value="radio">{{ fieldType('radio') }}</option>
            <option value="array">{{ fieldType('array') }}</option>
            <option value="file">{{ fieldType('file') }}</option>
          </select>
          <input
            v-if="form.element == 'field'"
            :name="`field-${field.id}-label`"
            v-model="form.label"
            class="flex-1 form-input ml-1 w-200 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            :placeholder="$t('fields.label_placeholder')"
          />
          <input
            v-if="['h2', 'h3', 'h4'].includes(form.element)"
            :name="`field-${field.id}-value`"
            v-model="form.value"
            class="form-input ml-1 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
          <textarea
            v-if="form.element == 'p'"
            :name="`field-${field.id}-value`"
            v-model="form.value"
            class="form-textarea mt-2 block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            cols="40"
            rows="10"
          />
        </div>
      </div>
      <div class="flex-shrink-0">
        <button
          type="button"
          @click="onEdit"
          :aria-label="$t('fields.edit_field')"
          class="w-4 text-gray-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        </button>
        <button
          type="button"
          @click="this.onDelete"
          :aria-label="$t('fields.remove_field')"
          class="ml-2 w-4 text-gray-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
    <div v-if="this.isEditing">
      <div v-if="form.element == 'field'" class="ml-5">
        <div class="mt-4">
          <label
            :for="`field-${field.id}-label`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.internal_id') }}
          </label>
          <div class="mt-1">
            <input
              :id="`field-${field.id}-id`"
              :name="`field-${field.id}-id`"
              v-model="form.id"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              placeholder="Intern veld dat gebruikt wordt in de rapportages en exports"
            />
          </div>
        </div>
        <div
          class="mt-4"
          v-if="['', 'text', 'email', 'date'].includes(form.type)"
        >
          <label
            :for="`field-${field.id}-autocomplete`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.autocomplete.title') }}
          </label>
          <select
            :id="`field-${field.id}-autocomplete`"
            :name="`field-${field.id}-autocomplete`"
            v-model="form.autocomplete"
            class="mt-2 ml-1 form-select transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option value="">-----</option>
            <option value="name">
              {{ $t('fields.autocomplete.fullname') }}
            </option>
            <option value="email">
              {{ $t('fields.autocomplete.email_address') }}
            </option>
            <option value="organization">
              {{ $t('fields.autocomplete.org_name') }}
            </option>
            <option value="postal-code">
              {{ $t('fields.autocomplete.postal_code') }}
            </option>
            <option value="country">
              {{ $t('fields.autocomplete.country') }}
            </option>
            <option value="street-address">
              {{ $t('fields.autocomplete.street_address') }}
            </option>
            <option value="url">
              {{ $t('fields.autocomplete.url') }}
            </option>
          </select>
          <p class="mt-1 text-sm text-gray-500">
            {{ $t('fields.autocomplete.label') }}
          </p>
        </div>
        <div class="mt-4">
          <label
            :for="`field-${field.id}-extra-info`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.additional_information') }}
          </label>
          <div class="mt-1">
            <textarea
              :id="`field-${field.id}-extra-information`"
              :name="`field-${field.id}-extra-information`"
              v-model="form.extra_information"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              :placeholder="$t('fields.additional_information_placeholder')"
            />
          </div>
        </div>
        <div v-if="form.type == 'longtext'" class="mt-4">
          <label
            :for="`field-${field.id}-maxlength`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.max_characters') }}
          </label>
          <div class="mt-1">
            <input
              :id="`field-${field.id}-maxlength`"
              :name="`field-${field.id}-maxlength`"
              type="number"
              v-model="form.maxlength"
              min="0"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              :placeholder="$t('fields.max_characters_placeholder')"
            />
          </div>
        </div>
        <div v-if="form.type == 'number'" class="mt-4">
          <label
            :for="`field-${field.id}-min`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.minimum') }}
          </label>
          <div class="mt-1">
            <input
              :id="`field-${field.id}-min`"
              :name="`field-${field.id}-min`"
              type="number"
              v-model="form.min"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              :placeholder="$t('fields.minimum_placeholder')"
            />
          </div>
        </div>
        <div v-if="form.type == 'number'" class="mt-4">
          <label
            :for="`field-${field.id}-max`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.maximum') }}
          </label>
          <div class="mt-1">
            <input
              :id="`field-${field.id}-max`"
              :name="`field-${field.id}-max`"
              type="number"
              v-model="form.max"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              :placeholder="$t('fields.maximum_placeholder')"
            />
          </div>
        </div>
        <div v-if="form.type == 'number'" class="mt-4">
          <label
            :for="`field-${field.id}-step`"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $t('fields.step_size') }}
          </label>
          <div class="mt-1">
            <input
              :id="`field-${field.id}-step`"
              :name="`field-${field.id}-step`"
              type="number"
              v-model="form.step"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              :placeholder="$t('fields.step_size_placeholder')"
            />
          </div>
        </div>
        <div v-if="form.type != 'array'" class="mt-4">
          <div class="relative flex items-start">
            <div class="flex items-center h-5">
              <input
                :id="`field-${field.id}-required`"
                :name="`field-${field.id}-required`"
                v-model="form.required"
                type="checkbox"
                class="focus:ring-blue-500 h-4 w-4 text-indigo-600 border-gray-500 rounded"
              />
            </div>
            <div class="ml-3 text-sm">
              <label
                :for="`field-${field.id}-required`"
                class="font-medium text-gray-700"
              >
                {{ $t('fields.mandatory') }}
              </label>
            </div>
          </div>
        </div>
        <div
          v-if="['dropdown', 'checkbox', 'radio'].includes(form.type)"
          class="mt-4"
        >
          <span class="block text-sm font-medium text-gray-700">
            {{ $t('fields.options') }}
          </span>
          <OptionsField field="field" v-model="form.options" class="mt-1" />
        </div>
        <div v-if="form.type == 'array'" class="mt-4">
          <span class="block text-sm font-medium text-gray-700">
            {{ $t('fields.array_fields') }}
          </span>
          <ArrayField field="field" v-model="form.fields" class="mt-1" />
        </div>
        <div v-if="form.type == 'file'" class="mt-4">
          <div class="relative flex items-start">
            <div class="flex items-center h-5">
              <input
                :id="`field-${field.id}-multiple`"
                :name="`field-${field.id}-multiple`"
                v-model="form.multiple"
                type="checkbox"
                class="focus:ring-blue-500 h-4 w-4 text-indigo-600 border-gray-500 rounded"
              />
            </div>
            <div class="ml-3 text-sm">
              <label
                :for="`field-${field.id}-multiple`"
                class="font-medium text-gray-700"
              >
                {{ $t('fields.accept_multiple_files') }}
              </label>
            </div>
          </div>
        </div>
        <div class="mt-4 text-sm">
          <ConditionalField
            :field="field"
            :fields="fields"
            v-model="form.conditional"
          />
        </div>
      </div>
      <div class="flex mt-8">
        <button type="submit" @click="onSave" class="btn-primary">
          {{ $t('fields.close') }}
        </button>
        <button @click="onCancel" class="btn-secondary ml-2">
          {{ $t('fields.cancel') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import OptionsField from './OptionsField.vue';
import ArrayField from './ArrayField.vue';
import ConditionalField from './ConditionalField.vue';

export default {
  name: 'FieldItem',
  components: {
    OptionsField,
    ArrayField,
    ConditionalField,
  },
  data() {
    return {
      isEditing: false,
      form: {},
    };
  },
  methods: {
    onEdit() {
      this.isEditing = !this.isEditing;
    },
    onSave(e) {
      e.preventDefault();
      this.isEditing = false;

      if (this.form.element === 'field') {
        this.$emit('onsave', this.form);
        return;
      }

      if (['h2', 'h3', 'h4', 'p'].includes(this.form.element)) {
        this.$emit('onsave', {
          element: this.form.element,
          value: this.form.value,
        });
        return;
      }
    },
    onDelete(e) {
      e.preventDefault();

      if (confirm(this.$t('fields.confirm_delete_field'))) {
        this.$emit('ondelete', e);
      }
    },
    onCancel(e) {
      e.preventDefault();
      this.isEditing = false;
      this.form = { ...this.field };
    },
    fieldType(type) {
      switch (type) {
        case '':
          return this.$t('fields.short_text');
        case 'longtext':
          return this.$t('fields.long_text');
        case 'email':
          return this.$t('fields.email');
        case 'number':
          return this.$t('fields.number');
        case 'date':
          return this.$t('fields.date');
        case 'datetime-local':
          return this.$t('fields.datetime');
        case 'dropdown':
          return this.$t('fields.dropdown');
        case 'checkbox':
          return this.$t('fields.checkbox');
        case 'radio':
          return this.$t('fields.radio');
        case 'array':
          return this.$t('fields.array');
        case 'file':
          return this.$t('fields.file');
      }
    },
  },
  props: {
    field: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    field: {
      immediate: true,
      handler: function (userFromProps) {
        if (userFromProps) {
          this.form = {
            ...this.form,
            ...userFromProps,
          };
        }
      },
    },
    form: {
      deep: true,
      handler: function (value) {
        if (
          ['', 'text', 'email', 'date'].includes(value.type) &&
          !value.autocomplete
        ) {
          this.form.autocomplete = '';
        }
        if (
          ['dropdown', 'checkbox', 'radio'].includes(value.type) &&
          !value.options
        ) {
          this.form.options = [];
        }
        if (value.type === 'array' && !value.fields) {
          this.form.fields = [];
        }
      },
    },
  },
};
</script>
